import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  ImageList,
  ImageListItem,
  Stack,
  Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { TProduct } from "@/api";
import {
  BooleanField,
  ChipField,
  DateField,
  DeleteWithConfirmButton,
  EditButton,
  FunctionField,
  Labeled,
  NumberField,
  ReferenceArrayField,
  ReferenceField,
  RichTextField,
  Show,
  Tab,
  TabbedShowLayout,
  SingleFieldList,
  TextField,
  TopToolbar,
  WithRecord,
  useRecordContext,
} from "react-admin";
import { ADMINS, BRANDS, CATEGORIES } from "../../config/resources";
import {
  productLocationTypeChoices,
  productTypeChoices,
  supplierProductApproveStatus,
} from "../../data/products";
import { useAdmin } from "../../hooks/admin";
import { ApproveButton } from "./actions/approveButton";
import { KitProductsReferenceArrayField } from "./fields/kit-products";
import { TaxClassificationProductField } from "./fields/tax-classification";
import { LocationProductField } from "./fields/location";
import { ProductSuppliersField } from "./fields/suppliers";
import { WarehouseStockTable } from "./fields/warehouse-stock";
import { InlineProductPriceField } from "./fields/inline-price";

const ProductShowActions = () => {
  const { hasPermission } = useAdmin();
  const record = useRecordContext();

  return (
    <TopToolbar>
      <ApproveButton />
      {hasPermission("product.update") && <EditButton />}
      {record && hasPermission("product.delete") && <DeleteWithConfirmButton />}
    </TopToolbar>
  );
};

const sectionTitleSx = {
  fontWeight: 600,
  fontSize: "0.95rem",
  letterSpacing: "0.02em",
  textTransform: "uppercase",
  color: "text.secondary",
  mb: 2,
};

export const ProductShow = () => {
  const { hasPermission } = useAdmin();

  if (!hasPermission("product.view")) return null;

  return (
    <Show actions={<ProductShowActions />}>
      <Box sx={{ p: { xs: 1, md: 2 } }}>
        <ProductHero />

        <TabbedShowLayout
          sx={{
            "& .RaTabbedShowLayout-content": {
              p: 0,
              pt: 2,
            },
          }}
        >
          <Tab label="Principal">
            <Grid container spacing={2}>
              <Grid item xs={12} lg={8}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <IdentityFields />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} lg={4}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography sx={sectionTitleSx}>Imágenes</Typography>
                    <ImagesGallery />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      alignItems={{ sm: "center" }}
                      justifyContent="space-between"
                      spacing={0.5}
                      sx={{ mb: 2 }}
                    >
                      <Typography sx={{ ...sectionTitleSx, mb: 0 }}>
                        Stock por bodega
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Edita el disponible y guarda con ✓
                      </Typography>
                    </Stack>
                    <WarehouseStockTable />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <SectionAccordion title="Distribuidores" defaultExpanded={false}>
                  <ProductSuppliersField />
                </SectionAccordion>
              </Grid>
            </Grid>
          </Tab>

          <Tab label="Avanzado">
            <AdvancedSections />
          </Tab>
        </TabbedShowLayout>
      </Box>
    </Show>
  );
};

const ProductHero = () => {
  const { hasPermission } = useAdmin();
  return (
    <WithRecord
      render={(product: TProduct) => (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Stack
              direction={{ xs: "column", md: "row" }}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
              spacing={2}
            >
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ minWidth: 0, flex: 1 }}
              >
                <Box
                  sx={{
                    width: { xs: 56, md: 72 },
                    height: { xs: 56, md: 72 },
                    borderRadius: 1.5,
                    overflow: "hidden",
                    bgcolor: "action.hover",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {product?.images?.[0]?.src ? (
                    <img
                      src={product.images[0].src}
                      alt={product.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      —
                    </Typography>
                  )}
                </Box>
                <Stack sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      lineHeight: 1.2,
                      fontSize: { xs: "1.15rem", md: "1.5rem" },
                      wordBreak: "break-word",
                    }}
                  >
                    {product?.name ?? "—"}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    sx={{ mt: 0.5 }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: "monospace" }}
                    >
                      SKU {product?.sku ?? "—"}
                    </Typography>
                    {product?.barCode && (
                      <>
                        <Typography
                          variant="caption"
                          color="text.disabled"
                        >
                          ·
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {product.barCode}
                        </Typography>
                      </>
                    )}
                  </Stack>
                </Stack>
              </Stack>

              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                flexWrap="wrap"
                justifyContent={{ xs: "flex-start", md: "flex-end" }}
              >
                <Chip
                  size="small"
                  variant="outlined"
                  label={product?.status ? "Activo" : "Inactivo"}
                  color={product?.status ? "success" : "default"}
                />
                {product?.featured && (
                  <Chip
                    size="small"
                    variant="outlined"
                    color="info"
                    label="Destacado"
                  />
                )}
                {product?.approvedStatus && (
                  <Chip
                    size="small"
                    variant="outlined"
                    color={
                      ["PENDING", "IN_REVIEW"].includes(product.approvedStatus)
                        ? "warning"
                        : product.approvedStatus === "APPROVED"
                        ? "success"
                        : "default"
                    }
                    label={
                      supplierProductApproveStatus.find(
                        ({ id }) => id === product.approvedStatus
                      )?.name ?? product.approvedStatus
                    }
                  />
                )}
                <Chip
                  size="small"
                  variant="outlined"
                  label={
                    productTypeChoices.find((t) => t.id === product?.type)
                      ?.name ?? product?.type
                  }
                />
              </Stack>
            </Stack>
            {hasPermission("brand.view") && product?.brandId && null}
          </CardContent>
        </Card>
      )}
    />
  );
};

const IdentityFields = () => {
  const { hasPermission } = useAdmin();
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <Stack spacing={1.5}>
          <Labeled label="Nombre">
            <TextField source="name" />
          </Labeled>
          <Labeled label="SKU">
            <TextField source="sku" />
          </Labeled>
          <Labeled label="Código de barras">
            <TextField source="barCode" emptyText="—" />
          </Labeled>
          <Labeled label="Descripción">
            <TextField source="description" emptyText="—" />
          </Labeled>
        </Stack>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Stack spacing={1.5}>
          <Labeled label="Marca">
            <ReferenceField
              source="brandId"
              reference={BRANDS}
              link={hasPermission("brand.view") ? "show" : false}
            >
              <TextField source="name" />
            </ReferenceField>
          </Labeled>
          <Labeled label="Categoría">
            <ReferenceField
              source="categoryId"
              reference={CATEGORIES}
              link={hasPermission("category.view") ? "show" : false}
            >
              <TextField source="name" />
            </ReferenceField>
          </Labeled>
          <FunctionField
            source="productOptions"
            label="Opciones"
            render={(record: TProduct) => {
              const entries = Object.entries(record.productOptions || {});
              if (!entries.length)
                return (
                  <Typography variant="body2" color="text.secondary">
                    —
                  </Typography>
                );
              return (
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {entries.map(([name, value]) => (
                    <Chip
                      key={name}
                      size="small"
                      variant="outlined"
                      label={`${name}: ${value}`}
                    />
                  ))}
                </Stack>
              );
            }}
          />
          <InlineProductPriceField
            label="Precio venta"
            source="wholesalePrice"
          />
          <InlineProductPriceField
            label="Precio compra"
            source="purchasePrice"
          />
        </Stack>
      </Grid>
      <Grid item xs={12}>
        <Labeled label="Descripción larga" fullWidth>
          <RichTextField source="longDescription" emptyText="—" />
        </Labeled>
      </Grid>
      <Grid item xs={12}>
        <KitProductsReferenceArrayField />
      </Grid>
    </Grid>
  );
};

const ImagesGallery = () => (
  <WithRecord
    render={(product: TProduct) => {
      const imgs = product?.images ?? [];
      if (!imgs.length)
        return (
          <Typography variant="body2" color="text.secondary">
            Sin imágenes.
          </Typography>
        );
      return (
        <ImageList cols={2} gap={6} sx={{ m: 0 }}>
          {imgs.map((img, i) => (
            <ImageListItem
              key={img.src + i}
              sx={{
                borderRadius: 1,
                overflow: "hidden",
                aspectRatio: "1 / 1",
                "& img": {
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                },
              }}
            >
              <img src={img.src} alt={`${product.name} ${i + 1}`} />
            </ImageListItem>
          ))}
        </ImageList>
      );
    }}
  />
);

const SectionAccordion = ({
  title,
  defaultExpanded,
  children,
}: {
  title: string;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}) => (
  <Accordion
    defaultExpanded={defaultExpanded}
    disableGutters
    sx={{
      "&:before": { display: "none" },
      borderRadius: 1,
      border: 1,
      borderColor: "divider",
      boxShadow: "none",
    }}
  >
    <AccordionSummary expandIcon={<ExpandMore />}>
      <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
    </AccordionSummary>
    <AccordionDetails>{children}</AccordionDetails>
  </Accordion>
);

const AdvancedSections = () => {
  const { hasPermission } = useAdmin();
  return (
    <Stack spacing={1}>
      <Accordion
        disableGutters
        sx={{
          "&:before": { display: "none" },
          boxShadow: "none",
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography>Precio y dimensiones</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <Labeled label="Retail price">
                <NumberField source="retailPrice" emptyText="—" />
              </Labeled>
            </Grid>
            <Grid item xs={6} md={3}>
              <Labeled label="Unit type">
                <NumberField source="unitType" emptyText="—" />
              </Labeled>
            </Grid>
            <Grid item xs={6} md={3}>
              <Labeled label="Unidades parciales">
                <BooleanField source="partialUnitsAllowed" />
              </Labeled>
            </Grid>
            <Grid item xs={12} />
            <Grid item xs={6} md={3}>
              <Labeled label="Alto">
                <TextField source="height" />
              </Labeled>
            </Grid>
            <Grid item xs={6} md={3}>
              <Labeled label="Ancho">
                <TextField source="width" />
              </Labeled>
            </Grid>
            <Grid item xs={6} md={3}>
              <Labeled label="Largo">
                <TextField source="length" />
              </Labeled>
            </Grid>
            <Grid item xs={6} md={3}>
              <Labeled label="Peso">
                <TextField source="weight" />
              </Labeled>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {hasPermission("product.show.tab.finance") && (
        <Accordion
          disableGutters
          sx={{
            "&:before": { display: "none" },
            boxShadow: "none",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography>Finanzas</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <Labeled label="% Impuesto">
                <NumberField source="taxPercentage" />
              </Labeled>
              <Labeled label="Clasificación tributaria">
                <TaxClassificationProductField />
              </Labeled>
              <Labeled label="Tipos de impuesto">
                <WithRecord
                  render={(p: TProduct) => (
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {(p?.taxesType ?? []).map((t) => (
                        <Chip key={t} size="small" label={t} variant="outlined" />
                      ))}
                      {(p?.taxesType ?? []).length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </Stack>
                  )}
                />
              </Labeled>
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      <Accordion
        disableGutters
        sx={{
          "&:before": { display: "none" },
          boxShadow: "none",
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography>Categorías visibles</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ReferenceArrayField
            reference={CATEGORIES}
            source="visibleCategories"
          >
            <SingleFieldList>
              <ChipField source="name" />
            </SingleFieldList>
          </ReferenceArrayField>
        </AccordionDetails>
      </Accordion>

      <Accordion
        disableGutters
        sx={{
          "&:before": { display: "none" },
          boxShadow: "none",
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography>Ubicación</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Labeled label="Tipo de ubicación">
              <FunctionField
                source="locationType"
                render={(record: any) =>
                  productLocationTypeChoices.find(
                    (t) => t.id === record.locationType
                  )?.name ?? "—"
                }
              />
            </Labeled>
            <LocationProductField />
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion
        disableGutters
        sx={{
          "&:before": { display: "none" },
          boxShadow: "none",
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography>Keywords</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <WithRecord
            render={(p: TProduct) => {
              const words = p?.keyWords ?? [];
              if (!words.length)
                return (
                  <Typography variant="body2" color="text.secondary">
                    —
                  </Typography>
                );
              return (
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {words.map((w) => (
                    <Chip key={w} size="small" label={w} variant="outlined" />
                  ))}
                </Stack>
              );
            }}
          />
        </AccordionDetails>
      </Accordion>

      <Accordion
        disableGutters
        sx={{
          "&:before": { display: "none" },
          boxShadow: "none",
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography>Auditoría</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Labeled label="ID">
                <TextField source="id" />
              </Labeled>
            </Grid>
            <Grid item xs={12} md={6} />
            <Grid item xs={12} md={6}>
              <Labeled label="Creado">
                <DateField source="createdAt" showTime />
              </Labeled>
            </Grid>
            <Grid item xs={12} md={6}>
              <Labeled label="Creado por">
                <ReferenceField
                  source="createdBy"
                  reference={ADMINS}
                  link={hasPermission("admin.view") ? "show" : false}
                >
                  <TextField source="email" />
                </ReferenceField>
              </Labeled>
            </Grid>
            <Grid item xs={12} md={6}>
              <Labeled label="Actualizado">
                <DateField source="updatedAt" showTime />
              </Labeled>
            </Grid>
            <Grid item xs={12} md={6}>
              <Labeled label="Actualizado por">
                <ReferenceField
                  source="updatedBy"
                  reference={ADMINS}
                  link={hasPermission("admin.view") ? "show" : false}
                >
                  <TextField source="email" />
                </ReferenceField>
              </Labeled>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
};
